import { Compass, Award, Fingerprint } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { toDisplayRows } from "../lib/identitySharing";

/**
 * The read-only rendering of a player's shared Identity & Vision, one card
 * per shared section, with a scannable label/value rhythm (issue #460).
 *
 * ============================================================
 * WHY THIS IS ITS OWN COMPONENT — THE ANTI-FORK (#497)
 * ============================================================
 * `PlayerIdentityPanel` (the coach's portal tab) owns the load, the failure
 * and empty states, and the coach framing. This owns only the *presentation*
 * of already-loaded sections, so the second surface that needs it —
 * #508's `PlayerProfileView`, the player/coach profile parity work — renders
 * the same cards by calling this, rather than forking a second layout that
 * drifts from this one. Whichever of #460/#508 lands first, the other reuses
 * this. `testIdPrefix` is a prop precisely so a second caller can keep its
 * own testids without this file knowing about them; it defaults to the
 * coach panel's existing `cp-identity`, so journey 13's assertions on
 * `cp-identity-<section>` keep passing unchanged.
 *
 * ============================================================
 * WHAT THE SCREENSHOT (2026-08-27) ASKED FOR
 * ============================================================
 * Before: ~20 tiny-grey-label / body-paragraph pairs in one undifferentiated
 * wall, full-width unreadable lines, list answers as comma run-ons. After:
 *
 *  - One Card per section (Player Vision / Strengths & Weaknesses / Game
 *    Identity), the house Card + heading-tile pattern used by
 *    `PlayerIdentity` and `CoachProfileView` — a new visual language is
 *    deliberately NOT invented.
 *  - A definition list inside each card, two-column at `lg` (the #474
 *    reading-width lesson: full-width 14px lines are unreadable), single
 *    column on a phone.
 *  - List-valued fields (`top_ambitions`, `preferred_formats`, and any other
 *    array the schemaless section carries) render as chips, honestly, one
 *    per item — not a comma run-on. `toDisplayRows` carries the `isList`
 *    flag; this file decides chips vs prose.
 *
 * ============================================================
 * IT STILL SHOWS ONLY WHAT WAS SHARED, AND NOTHING EMPTY
 * ============================================================
 * The sharing granularity is upstream: `presentSections` already drops a
 * section the payload omits, and `toDisplayRows` already drops an
 * unanswered field (the #292 non-oracle rule — a rendered empty row asserts
 * "the player was asked this and said nothing", a claim this screen has no
 * business making). This component keeps both: a section that yields no rows
 * renders no card, so an ungranted section — and the degenerate case of a
 * shared-but-blank one — leaves no empty card behind. It renders `null` when
 * nothing is showable, so a caller can decide what to say in that case.
 */

const SECTION_ICONS = {
  vision: Compass,
  strengths_weaknesses: Award,
  game_identity: Fingerprint,
};

/**
 * `appearance="r5"` (#1221 slice 2, `CoachPlayerGoals.dc.html`'s open Identity
 * card): the same sections and rows as kit panels instead of the shipped
 * `Card`, a list answer as one line of text like every other answer. Unset,
 * the output is the shipped one, byte for byte (`PlayerProfileView` and the
 * flag-off portal pass none). The testids are the same in both.
 */
function SharedIdentityViewR5({ cards, testIdPrefix }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }} data-testid={`${testIdPrefix}-sections`}>
      {cards.map(({ section, rows }) => (
        <div
          key={section.key}
          data-testid={`${testIdPrefix}-${section.key}`}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            padding: 12,
            minWidth: 0,
            background: "var(--nw-page)",
            border: "1px solid var(--nw-line)",
            borderRadius: "var(--nw-radius-control)",
          }}
        >
          <h4
            style={{ margin: 0, fontSize: 10, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--nw-ink-muted)", overflowWrap: "anywhere" }}
          >
            {section.label}
          </h4>
          {rows.map((row) => (
            <div key={row.key} style={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, color: "var(--nw-ink-muted)", overflowWrap: "anywhere" }}>{row.label}</div>
              <div
                data-testid={`${testIdPrefix}-${section.key}-${row.key}`}
                style={{ fontSize: 14, lineHeight: 1.5, color: "var(--nw-ink)", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
              >
                {row.value}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export default function SharedIdentityView({ sections, testIdPrefix = "cp-identity", appearance }) {
  const cards = (sections || [])
    .map((section) => ({ section, rows: toDisplayRows(section.content) }))
    .filter(({ rows }) => rows.length > 0);

  if (cards.length === 0) return null;
  if (appearance === "r5") return <SharedIdentityViewR5 cards={cards} testIdPrefix={testIdPrefix} />;

  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-sections`}>
      {cards.map(({ section, rows }) => {
        const Icon = SECTION_ICONS[section.key] || Compass;
        return (
          <Card
            key={section.key}
            className="border-slate-200 min-w-0"
            data-testid={`${testIdPrefix}-${section.key}`}
          >
            <CardContent className="p-4 sm:p-5 space-y-4 min-w-0">
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: "#0B2E5915" }}
                >
                  <Icon size={16} color="#0B2E59" />
                </div>
                <h3 className="font-display font-bold text-base text-slate-900 break-words">
                  {section.label}
                </h3>
              </div>

              <dl className="grid gap-x-8 gap-y-4 lg:grid-cols-2">
                {rows.map((row) => (
                  <div key={row.key} className="min-w-0">
                    <dt className="text-[11px] uppercase tracking-[0.12em] text-slate-500 break-words">
                      {row.label}
                    </dt>
                    {row.isList ? (
                      <dd
                        className="mt-1.5 flex flex-wrap gap-1.5"
                        data-testid={`${testIdPrefix}-${section.key}-${row.key}`}
                      >
                        {row.items.map((item, i) => (
                          <span
                            key={i}
                            className="px-2.5 py-1 rounded-full text-[13px] font-medium break-words"
                            style={{ background: "#84C5FF33", color: "#0B2E59" }}
                          >
                            {item}
                          </span>
                        ))}
                      </dd>
                    ) : (
                      <dd
                        className="text-sm text-slate-900 mt-1 break-words whitespace-pre-wrap"
                        data-testid={`${testIdPrefix}-${section.key}-${row.key}`}
                      >
                        {row.value}
                      </dd>
                    )}
                  </div>
                ))}
              </dl>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
